import React from "react"

import classNames from "classnames"

import { useEvent } from "shared/lib/useEvent"
import { WithClassName } from "shared/types/WithClassName"

import { useGlobalSearchContext } from "../GlobalSearchContext"
import { useGlobalSearchNavigationContext } from "../GlobalSearchNavigationContext"

interface GlobalSearchListboxProps extends WithClassName {
  children: React.ReactNode
}

export const GlobalSearchListbox = React.memo(
  ({ className, children }: GlobalSearchListboxProps) => {
    const {
      listboxId,
      activeIndex,
      getItemId,
      activateItem,
      onContainerKeyDown,
      keyboardActiveRef
    } = useGlobalSearchNavigationContext()
    const { isLoading } = useGlobalSearchContext()

    const handleMouseMove = useEvent(() => {
      keyboardActiveRef.current = false
    })

    const handleMouseLeave = useEvent(() => {
      if (!keyboardActiveRef.current) activateItem(null)
    })

    const handleFocus = useEvent(() => {
      if (activeIndex === null) activateItem(0)
    })

    const handleBlur = useEvent((e: React.FocusEvent) => {
      if (
        (e.currentTarget as HTMLElement).contains(
          e.relatedTarget as Node | null
        )
      )
        return
      const input = document.querySelector<HTMLElement>(
        `[aria-controls="${listboxId}"]`
      )
      if (input === e.relatedTarget) return
      activateItem(null)
    })

    return (
      <div
        id={listboxId}
        role="listbox"
        tabIndex={0}
        className={classNames("global-search__list-box", className)}
        aria-busy={isLoading}
        aria-activedescendant={
          activeIndex !== null ? getItemId(activeIndex) : undefined
        }
        onKeyDown={onContainerKeyDown}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {children}
      </div>
    )
  }
)
